import { Address } from "@helium/crypto";
import { LocationMarkerIcon, SearchIcon } from "@heroicons/react/outline";
import { useCombobox } from "downshift";
import { debounce, every, head, isEmpty, toLower, values, words } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useAuthentication } from "../context/authentication";
import { useContacts } from "../context/contacts";
import { useGoogleMapsWrapper } from "../context/googleMaps";
import {
  convertEtlHotspotSearchResponse,
  HotspotsSearchResponse
} from "../context/hotspots/HotspotsProvider";
import { useLocations } from "../context/locations";
import { isHotspotLocation } from "../context/locations/LocationFormSidebar";
import { useViewport } from "../context/viewport";
import classNames from "../utils/classNames";
import { avatarsApi, breakpoints } from "../utils/constants";
import {
  GeoResult,
  getDefaultGeoService,
  isGeoResult
} from "../utils/geoservices";
import { GeoServices } from "../utils/geoservices/geoservices";
import getCoordinates, {
  getUnshortenedGmapsUrl
} from "../utils/getCoordinates";
import { formatHotspotName, formatHotspotPlace } from "../utils/hotspotUtils";
import { h3ToCenterChildMemoized } from "../utils/memoizedMethods";
import { Contact, EtlHotspot, HotspotLocation } from "../utils/types";
import useMapCenter from "../utils/useMapCenter";
import CenteredLoading from "./CenteredLoading";
import ContactComponent from "./ContactComponent";
import DisplayLabels from "./labels/DisplayLabels";
import StatusDot from "./StatusDot";

interface Props {
  onClose: () => void;
}

const QuickSearchModal: React.FC<Props> = ({ onClose }) => {
  const history = useHistory();
  const { locations, setEditLocationConfig, findLocationIdByCoordinates } =
    useLocations();
  const [loading, setLoading] = useState(false);
  const { contacts } = useContacts();
  const [geoResults, setGeoResults] = useState<GeoResult[]>([]);
  const [fetchedHotspots, setFetchedHotspots] = useState<EtlHotspot[]>([]);
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.sm, [width]);
  const [center, boundaries] = useMapCenter((state) => [
    state.center,
    state.boundaries
  ]);
  const setZoom = useMapCenter((state) => state.setZoom);
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const setCenter = useMapCenter((state) => state.setCenter);
  const { status } = useGoogleMapsWrapper();
  const { pathname } = useLocation();
  const explorerPageMatch = matchPath<{
    tab?: string; // hotspots, locations, hexes
  }>(pathname, {
    path: "/:tab"
  });
  const [filteredLocations, setFilteredLocations] = useState<HotspotLocation[]>(
    []
  );
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const { authenticationState } = useAuthentication();

  const escFunction = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    },
    [onClose]
  );

  const handleUserPressKey = useCallback((event) => {
    switch (event.shiftKey && event.key) {
      case "Home":
        event.target.select();
        break;
      case "ArrowUp":
        event.target.setSelectionRange(0, event.target.selectionEnd);
        break;
      case "ArrowDown":
        event.target.setSelectionRange(event.target.selectionStart, 999);
        break;
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    document.addEventListener("keydown", handleUserPressKey, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
      document.removeEventListener("keydown", handleUserPressKey, false);
    };
  }, [escFunction, handleUserPressKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchResults = useCallback(
    debounce(async (searchInput: string | undefined) => {
      if (!searchInput || searchInput.trim().length <= 4) {
        setFetchedHotspots([]);
        setGeoResults([]);
        return;
      }

      setLoading(true);
      const expandedGmapsUrl = await getUnshortenedGmapsUrl(searchInput);

      const coordinates = await getCoordinates(expandedGmapsUrl ?? searchInput);

      if (Address.isValid(searchInput)) {
        setLoading(false);

        history.push(`/hotspots/${searchInput}/status`);
        onClose();
        return;
      }

      const findHotspotsUrl = stringifyUrl({
        url: `${process.env.REACT_APP_ETL_API}/hotspots/search-lean`,
        query: {
          name: searchInput,
          limit: 5,
          ...(centerInH3HexRes4
            ? {
                proximity_hex: h3ToCenterChildMemoized(centerInH3HexRes4, 12)
              }
            : {})
        }
      });

      fetch(findHotspotsUrl)
        .then((res) => res.json())
        .then((res: HotspotsSearchResponse) =>
          setFetchedHotspots(convertEtlHotspotSearchResponse(res.data) || [])
        );

      const geoService = getDefaultGeoService(
        status.current,
        !!expandedGmapsUrl ? GeoServices.GMAPS : GeoServices.MAPBOX // For now, use mapbox in quick search to test how much it reduces the google maps costs
      );

      try {
        const results = await geoService.search(
          coordinates || expandedGmapsUrl || searchInput,
          {
            boundaries,
            center
          }
        );

        setGeoResults(results);

        if (
          (coordinates || expandedGmapsUrl) &&
          !isEmpty(results) &&
          authenticationState === "authenticated"
        ) {
          setEditLocationConfig({
            show: true,
            location: head(results),
            onSuccess: (location) =>
              isHotspotLocation(location) &&
              history.push(`/locations/${location.uuid}/super-simulations`)
          });
          onClose();
        }
      } catch (error) {
        console.error(error);
      }

      setLoading(false);
    }, 500),
    [onClose, history, setEditLocationConfig, authenticationState]
  );

  const searchResults = useMemo(
    () => [
      ...filteredContacts,
      ...filteredLocations,
      ...fetchedHotspots,
      ...geoResults
    ],
    [fetchedHotspots, filteredContacts, filteredLocations, geoResults]
  );

  const updateFilteredContacts = useCallback(
    (searchInput: string) => {
      const filteredResults = values(contacts).filter((contact) => {
        if (searchInput) {
          const searchInputWords = words(searchInput).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(contact.name).includes(word) ||
              toLower(contact.email || "").includes(word) ||
              toLower(contact.phone || "").includes(word)
          );
        }
        return false;
      });

      setFilteredContacts(filteredResults);
    },
    [contacts]
  );

  const updateFilteredLocations = useCallback(
    (searchInput: string) => {
      const filteredResults = values(locations).filter((location) => {
        if (searchInput) {
          const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(location.name).includes(word) ||
              toLower(location.address || "").includes(word)
          );
        }
        return false;
      });

      setFilteredLocations(filteredResults);
    },
    [locations]
  );

  const {
    getInputProps,
    getComboboxProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useCombobox<EtlHotspot | Contact | HotspotLocation | GeoResult>({
    isOpen: true,
    items: searchResults,
    onSelectedItemChange: async ({ selectedItem }) => {
      switch (getResultType(selectedItem)) {
        case "hotspot": {
          history.push(`/hotspots/${(selectedItem as EtlHotspot).id}/status`);
          break;
        }
        case "contact": {
          history.push(`/contacts/${(selectedItem as Contact).uuid}/info`);
          break;
        }
        case "location": {
          history.push(
            `/locations/${
              (selectedItem as HotspotLocation).uuid
            }/super-simulations`
          );
          break;
        }
        case "place": {
          const place = selectedItem as GeoResult;

          const [lng, lat] = place.center;

          setCenter([lng, lat]);
          setZoom([15]);

          if (
            (place.types.includes("address") ||
              place.types.includes("route") ||
              place.types.includes("street_address")) &&
            authenticationState === "authenticated"
          ) {
            const locationId = findLocationIdByCoordinates(lat, lng)?.uuid;
            if (locationId) {
              history.push(`/locations/${locationId}`);
              break;
            }

            setEditLocationConfig({
              show: true,
              location: place,
              onSuccess: (location) =>
                isHotspotLocation(location) &&
                history.push(`/locations/${location.uuid}/super-simulations`)
            });

            onClose();
          } else if (
            !explorerPageMatch?.params.tab ||
            !["hotspots", "locations", "hexes", "contacts"].includes(
              explorerPageMatch.params.tab
            )
          ) {
            history.push("/hotspots");
          }
        }
      }

      onClose();
    },
    itemToString: (item) => {
      switch (getResultType(item)) {
        case "hotspot":
          return formatHotspotName(item as any) as string;
        case "contact":
          return (item as Contact).name;
        case "location":
          return (item as HotspotLocation).name;
        case "place":
          return (item as GeoResult).name;
        default:
          return "";
      }
    },
    onInputValueChange: ({ inputValue }) => {
      updateFilteredLocations(inputValue || "");
      updateFilteredContacts(inputValue || "");
      debouncedFetchResults(inputValue);
    }
  });

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto text-center">
      <div
        className="fixed inset-0 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className="sm:py-16 py-4 px-4 sm:px-0">
        <div
          className="sm:inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:max-w-sm sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div
            className="flex flex-col"
            style={{ maxHeight: `calc(100vh - ${isMobile ? "2rem" : "8rem"})` }}
          >
            <div className="flex px-6 py-4">
              <form className="w-full flex md:ml-0">
                <div
                  className="h-8 w-full text-gray-400 group focus-within:text-gray-600 flex items-center"
                  {...getComboboxProps({}, { suppressRefError: true })}
                >
                  <div
                    className="flex-none flex items-center pointer-events-none"
                    aria-hidden="true"
                  >
                    {loading ? (
                      <CenteredLoading className="h-5 w-5 text-gray-400" />
                    ) : (
                      <SearchIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    )}
                  </div>

                  <input
                    className="block w-full h-full p-2 border-transparent text-gray-900 dark:text-white dark:bg-gray-800 placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent sm:text-sm"
                    autoFocus
                    placeholder="Find hotspots, locations, contacts..."
                    type="search"
                    {...getInputProps({}, { suppressRefError: true })}
                  />

                  <button
                    type="button"
                    className="flex-none p-1 text-sm text-gray-400 rounded-md border border-gray-200 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 font-light"
                    onClick={onClose}
                  >
                    esc
                  </button>
                </div>
              </form>
            </div>

            <ul
              className="flex-1 relative z-0 overflow-y-auto"
              {...getMenuProps({}, { suppressRefError: true })}
            >
              {!isEmpty(filteredContacts) && (
                <div className="z-10 sticky top-0 border-t border-b border-gray-200 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 px-6 py-1 text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center">
                  <h3 className="ml-2">Contacts</h3>
                </div>
              )}

              {filteredContacts.map((contact, index) => (
                <li key={contact.uuid}>
                  <div
                    className={classNames(
                      "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-transparent border-b border-gray-200 dark:border-gray-500",
                      highlightedIndex === index &&
                        "ring-2 ring-inset ring-hotspotty-400 dark:ring-white"
                    )}
                    {...getItemProps({
                      item: contact,
                      index: index
                    })}
                  >
                    <ContactComponent contactId={contact.uuid} />
                  </div>
                </li>
              ))}

              {!isEmpty(filteredLocations) && (
                <div className="z-10 sticky top-0 border-t border-b border-gray-200 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 px-6 py-1 text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center">
                  <h3 className="ml-2">Locations</h3>
                </div>
              )}

              {filteredLocations.map((location, index) => (
                <li key={location.uuid}>
                  <div
                    className={classNames(
                      "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-transparent border-b border-gray-200 dark:border-gray-500",
                      highlightedIndex === filteredContacts.length + index &&
                        "ring-2 ring-inset ring-hotspotty-400 dark:ring-white"
                    )}
                    {...getItemProps({
                      item: location,
                      index: filteredContacts.length + index
                    })}
                  >
                    <div className="flex-shrink-0">
                      <span className="inline-block relative">
                        <LocationMarkerIcon className="h-10 w-10 text-gray-500 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-white" />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 focus:outline-none">
                      <span className="absolute inset-0" aria-hidden="true" />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {location.name}
                      </p>
                      <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                        {location.address}
                      </p>
                      <DisplayLabels linkedUuid={location.uuid} size="small" />
                    </div>
                  </div>
                </li>
              ))}

              {!isEmpty(fetchedHotspots) && (
                <div className="z-10 sticky top-0 border-t border-b border-gray-200 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 px-6 py-1 text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center">
                  <h3 className="ml-2">Hotspots</h3>
                </div>
              )}

              {fetchedHotspots.map((hotspot, index) => (
                <li key={hotspot.id}>
                  <div
                    className={classNames(
                      "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-transparent border-b border-gray-200 dark:border-gray-500",
                      highlightedIndex ===
                        filteredContacts.length +
                          filteredLocations.length +
                          index &&
                        "ring-2 ring-inset ring-hotspotty-400 dark:ring-white"
                    )}
                    {...getItemProps({
                      item: hotspot,
                      index:
                        filteredContacts.length +
                        filteredLocations.length +
                        index
                    })}
                  >
                    <div className="flex-shrink-0">
                      <span className="inline-block relative">
                        <img
                          className="h-10 w-10 rounded-full"
                          src={stringifyUrl({
                            url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                            query: { w: 40, h: 40 }
                          })}
                          alt="Hotspot avatar"
                        />
                        <StatusDot
                          hotspot={hotspot}
                          className="absolute top-0 right-0 block"
                          withoutTooltip
                        />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 focus:outline-none">
                      <span className="absolute inset-0" aria-hidden="true" />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatHotspotName(hotspot as any)}
                      </p>
                      <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                        {formatHotspotPlace(hotspot.place)}
                      </p>
                      <DisplayLabels
                        linkedUuid={hotspot.id}
                        size="small"
                        contextWalletAddresses={[hotspot.owner]}
                      />
                    </div>
                  </div>
                </li>
              ))}

              {!isEmpty(geoResults) && (
                <div className="z-10 sticky top-0 border-t border-b border-gray-200 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 px-6 py-1 text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center">
                  <h3 className="ml-2">Addresses</h3>
                </div>
              )}

              {geoResults.map((place, index) => {
                const [lng, lat] = place.center;
                return (
                  <li key={place.id}>
                    <div
                      className={classNames(
                        "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-transparent border-b border-gray-200 dark:border-gray-500",
                        highlightedIndex ===
                          filteredContacts.length +
                            filteredLocations.length +
                            fetchedHotspots.length +
                            index &&
                          "ring-2 ring-inset ring-hotspotty-400 dark:ring-white"
                      )}
                      {...getItemProps({
                        item: place,
                        index:
                          filteredContacts.length +
                          filteredLocations.length +
                          fetchedHotspots.length +
                          index
                      })}
                    >
                      <div className="flex-shrink-0">
                        <span className="inline-block relative">
                          {!!findLocationIdByCoordinates(lat, lng)?.uuid ? (
                            <LocationMarkerIcon className="h-10 w-10 text-gray-500 dark:text-gray-300" />
                          ) : (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={stringifyUrl({
                                url: `${avatarsApi}/initials/${getPlaceCountryCode(
                                  place
                                )}.svg`,
                                query: { w: 40, h: 40 }
                              })}
                              alt="Place avatar"
                            />
                          )}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 focus:outline-none">
                        <span className="absolute inset-0" aria-hidden="true" />
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {place.name.split(", ").map((namePart, j) => (
                            <span
                              key={`${namePart}-${index}-${j}`}
                              className="block"
                            >
                              {namePart.replace(",", "")}
                            </span>
                          ))}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickSearchModal;

export const getPlaceCountryCode = (place: GeoResult) => {
  const countryContext = place.components?.find(
    (component) => component.type === "country"
  );

  return countryContext?.shortName || "/";
};

const getResultType = (
  result: EtlHotspot | Contact | HotspotLocation | GeoResult | null | undefined
) => {
  if (!result) {
    return "null";
  }

  if (isGeoResult(result)) {
    return "place";
  }

  if ("linked_location_ids" in result) {
    return "contact";
  }

  if ("linked_contact_ids" in result) {
    return "location";
  }

  return "hotspot";
};
